<!--

============================================================================================================
||    WebWidgets page                                                                                        ||
============================================================================================================

|   Description
------------------------------------------------------------------------------------------------------------
  Page for managing Web Widgets.

|   Features
------------------------------------------------------------------------------------------------------------
  - No widgets created view
  - List of widgets
  - Filter widgets
  - Create new widget

-->

<template>
  <div
    class="flows-wrapper w-100"
    :class="{'announcement-space': showAnnouncement }" >
    <Spinner
      v-if="!widgetsLoaded"
      class="mt-5"
      :thickness="2" />
    <InfoCard
      v-else-if="widgetsLoaded && webWidgets.length === 0"
      :image="require('@/assets/img/SB-Images/placeholder_widget.svg')"
      title="Set up your first AI web widget"
      subtitle="Add a widget to your website so visitors can reach you instantly."
      :background-color1="SBColors.white"
      :background-color2="SBColors.greyUltralight"
      :max-width="761"
      in-line
      reverse
      padding="41px 37px 35px" >
      <template slot="action-slot">
        <b-button
          id="create-widget-btn"
          class="flex center-all"
          @click="createNewWidget" >
          <SBIcon
            class="mr-2"
            :icon="SBIconList.PLUS"
            :color="SBColors.blackPrimary"
            :size="8"/>
          <span>Create</span>
        </b-button>
      </template>
    </InfoCard>
    <ScrollArea
      v-else-if="widgetsLoaded && webWidgets.length > 0"
      ref="widgetsScrollbar" >
      <PageTitle
        image="assets/img/SB-Icons/AI-stars.svg"
        title="Web Widget"
        class="sb-margin-x-40" />
      <div class="sticky-top">
        <div class="mask" />
        <PageFilters
          v-model="filterData"
          class="sb-margin-x-40 flow-filters mb-3"
          show-search
          no-debounce
          :filter-numbers="false"
          search-text="Search widget..."
          @add-new="createNewWidget" />
      </div>

      <BlockTitle
        v-if="webWidgetsListData.filter(w => !w.hide).length"
        class="sb-margin-x-40"
        :title="`WEB WIDGET (${webWidgetsListData.filter(w => !w.hide).length.toString()})`" />
      <div
        v-if="widgetsLoaded && filterData.search && !webWidgetsListData.some((w) => !w.hide)"
        class="no-data flex column center-all">
        <span
          v-if="filterData.search"
          class="wrapper flex center-all">
          <span class="emoji">😐</span>
          <p>
            We couldn't find anything for
            <strong v-if="filterData.search">{{ filterData.search }}</strong>
          </p>
        </span>
        <span
          v-if="filterData.length"
          class="clear-filters-btn"
          @click="clearFilter" >Clear filter</span>
      </div>
      <template v-else>
        <DraggableList
          v-model="webWidgetsListData"
          pointer
          disable
          toggle-tooltip="Widget is turned"
          hide-image
          @selected="widgetSelected"
          @remove="deleteWidget"
          @activate="activateWidget" />
      </template>
    </ScrollArea>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ScrollArea from '@/components/common/ScrollArea';
import InfoCard from '@/components/common/InfoCard2';
import PageTitle from '@/components/common/PageTitle';
import PageFilters from '@/components/common/PageFilters';
import BlockTitle from '@/components/common/BlockTitle';
import DraggableList from '@/components/common/DraggableList';
import Spinner from '@/components/common/Spinner';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import api from '@/helpers/api';
import ToasterMixin from '@/mixins/Toaster';

export default {
  name: 'WebWidgets',
  components: {
    ScrollArea,
    InfoCard,
    PageTitle,
    PageFilters,
    BlockTitle,
    DraggableList,
    Spinner,
    SBIcon,
  },
  mixins: [
    ToasterMixin,
  ],
  data() {
    return {
      webWidgets: [],
      createWidget: false,
      loadingSchema: true,
      creatingWidget: false,
      widgetsLoaded: false,
      preventAbort: false,
      currentScroll: 0,
      filterData: {
        search: '',
      },
      flags: {},
      SBIconList,
      SBColors,
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
      permissionDenied: 'user/permissionDenied',
      showAnnouncement: 'system/showAnnouncement',
      organisationFeatures: 'organisation/features',
      // webWidgets: 'organisation/webWidgets',
      actionInProgress: 'automation/actionInProgress',
    }),
    webWidgetsListData() {
      return this.webWidgets.map(widget => ({
        ...widget,
        label: widget.name,
        hide: false,
        active: widget.is_active,
      }));
    },
  },
  watch: {
    filterData() {
      this.filterWidgetsList();
    },
    permissionDenied() {
      this.widgetsLoaded = true;
    },
  },
  mounted() {
    if (this.currentOrganisation.id) {
      this.fetchWebWidgets()
        .then(() => {
          this.widgetsLoaded = true;
        });
    }
  },
  methods: {
    ...mapActions({
      // fetchWebWidgets: 'organisation/fetchWebWidgets',
      setWebWidgets: 'organisation/setWebWidgets',
    }),
    fetchWebWidgets() {
      const request = api('GET', `/organisation/${this.currentOrganisation.id}/widgets`, null, null, 'w');
      request.then((response) => {
        if (response.status === 200) {
          this.webWidgets = response.data;
          this.setWebWidgets(response.data);
        }
      });
      return request;
    },
    filterWidgetsList() {
      this.webWidgetsListData.forEach((webWidget) => {
        let hide = true;
        if (!this.filterData.search || webWidget.label.toLowerCase().indexOf(this.filterData.search.toLowerCase()) >= 0) {
          hide = false;
        }
        webWidget.hide = hide;
      });
    },
    clearFilter() {
      this.filterData = {
        search: '',
      };
    },
    widgetSelected(widget) {
      this.$router.push({
        name: 'WebWidgetEdit',
        params: {
          widgetId: widget.id,
        },
      });
    },
    createNewWidget() {
      this.$router.push({
        name: 'WebWidgetCreate',
        params: {
          organisationId: this.currentOrganisation.id,
          widgetId: null,
        },
      });
    },
    deleteWidget(widget) {
      this.$confirmPluginModal.show({
        buttonVariant: 'RED',
        title: 'Delete web widget',
        message: `Are you sure you want to delete widget <b>${widget.label}</b>?`,
        confirmButtonText: 'Delete',
        showUndoWarning: false,
        autoClose: 'NO',
        confirmCallback: () => {
          api('DELETE', `/organisation/${this.currentOrganisation.id}/widgets/${widget.id}/delete`, null, null, 'w')
            .then((response) => {
              if (response.status >= 200 && response.status < 300) {
                this.webWidgets = this.webWidgets.filter(w => w.id !== widget.id);
                this.showToaster('Web widget has been successfully deleted.', null, null, 'OK');
              } else {
                this.showToaster('We couldn\'t delete web widget. Please, try again later.', null, null, 'error');
              }
            })
            .finally(() => {
              this.$confirmPluginModal.close();
            });
        },
      });
    },
    activateWidget(widget) {
      console.log('ACTIVATE WIDGET', widget);
      widget.is_active = !widget.is_active;
      const payload = { ...widget };
      delete payload.label;
      delete payload.active;
      delete payload.id;
      delete payload.identifier;
      delete payload.updated_at;
      delete payload.created_at;

      console.log('PAYLOAD', payload);
      api('PUT', `/organisation/${this.currentOrganisation.id}/widgets/${widget.id}/update`, payload, null, 'w')
        .catch((error) => {
          widget.is_active = !widget.is_active;
          this.webWidgets = [...this.webWidgets];
          this.showToaster('Something went wrong. Please try again.', null, null, 'error');
        });
    },
    toTop() {
      this.$refs.stepperScroll.scrollTo(0);
    },
  },
};
</script>

<style lang="scss" scoped>
.flows-wrapper {
  background-color: $white;
  z-index: 998;
}

.stepper-wrapper {
  padding-top: 24px;
  padding-bottom: 24px;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: $white;
}

.mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: $white;
  z-index: -1;
}

.no-data {
  padding: 40px;
  text-align: center;
  border-top: none;

  .wrapper {
    margin-bottom: 16px;

    .emoji {
      font-size: 24px;
      margin-right: 8px;
    }
  }

  .clear-filters-btn {
    color: $bluePrimary;
    cursor: pointer;
    text-decoration: underline;
  }
}
</style>
